/*! ============================================================
    RÓTA · the rotation engine
    Lund Studio · v1.0 · © 2026 Lund Studio LLC · licensed, not free

    Old Norse róta — to stir, to turn over.

    Content that never sits still. Give RÓTA a pool of items and a
    slot to put them in, and it decides what shows — by the day, by
    the hour of the day, and by the visit, so a reload or a back
    button is a different page and nobody can predict what they get.

    Three clocks, and you choose how many of them turn:
      DAY    the pool re-deals at local midnight
      BAND   dawn / day / dusk / night each get their own deal
      VISIT  every page view moves the bag on

    And one rule that makes it feel designed rather than random:
    RÓTA deals from a shuffled bag, not a dice roll. Nothing repeats
    until the pool is exhausted. A visitor can hit reload twenty
    times and see twenty different things.

    ~4KB, no build step, no dependencies, no network.
    ============================================================ */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.ROTA = factory();
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var PHI = 1.6180339887498948;

  /* ---- seeded PRNG (mulberry32): same seed, same sequence, everywhere ---- */
  function rng(seed) {
    var a = seed >>> 0;
    return function () {
      a |= 0; a = a + 0x6D2B79F5 | 0;
      var t = Math.imul(a ^ a >>> 15, 1 | a);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }

  function hash(str) {
    var h = 2166136261 >>> 0;
    for (var i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
    return h >>> 0;
  }

  /* ---- the three clocks ---- */
  function dayIndex(d) {
    d = d || new Date();
    return Math.floor((d - new Date(d.getFullYear(), 0, 0)) / 86400000) + d.getFullYear() * 1000;
  }
  var BANDS = ['night', 'dawn', 'day', 'dusk'];
  function band(d) {
    var h = (d || new Date()).getHours();
    if (h < 6) return 0;        /* night  · 00–06 */
    if (h < 11) return 1;       /* dawn   · 06–11 */
    if (h < 17) return 2;       /* day    · 11–17 */
    if (h < 21) return 3;       /* dusk   · 17–21 */
    return 0;                   /* night again */
  }
  function bandName(d) { return BANDS[band(d)]; }

  /* ---- the walk: shuffle once per clock, then step through it ----
     The order is decided by the day (and the band, if you let it turn), so it
     is stable and reproducible. The visit counter is an OFFSET into that order,
     not a reshuffle — which is why a reload shows something new and nothing
     repeats until the pool has been spent. */
  function shuffled(n, seed) {
    var r = rng(seed), order = [], i, j, t;
    for (i = 0; i < n; i++) order.push(i);
    for (i = n - 1; i > 0; i--) { j = Math.floor(r() * (i + 1)); t = order[i]; order[i] = order[j]; order[j] = t; }
    return order;
  }

  /* the visit counter: what makes reload and back-button differ */
  function visitTick(key, advance) {
    var k = 'rota.visit.' + key, n = 0;
    try {
      n = parseInt(sessionStorage.getItem(k), 10) || 0;
      if (advance) { n += 1; sessionStorage.setItem(k, n); }
    } catch (e) { n = Math.floor(Math.random() * 1e6); }
    return n;
  }

  /* which clocks are allowed to turn the ORDER (never the visit — that is the offset) */
  function seedFor(opts, now) {
    var parts = [opts.key || 'rota'];
    if (opts.day !== false) parts.push('d' + dayIndex(now));
    if (opts.band !== false) parts.push('b' + band(now));
    if (opts.salt) parts.push(String(opts.salt));
    return hash(parts.join('|'));
  }

  /* ---- pick: the whole engine in one call ---- */
  function pick(pool, opts) {
    opts = opts || {};
    var now = opts.now || new Date();
    var key = opts.key || 'rota';

    /* scheduling and band-targeting filter the pool before anything is dealt */
    var live = pool.filter(function (item) {
      if (!item || typeof item !== 'object') return true;
      if (item.from && new Date(item.from) > now) return false;
      if (item.until && new Date(item.until) < now) return false;
      if (item.band && item.band !== bandName(now)) return false;
      return true;
    });
    var pinned = live.filter(function (i) { return i && i.pin; });
    var rest = live.filter(function (i) { return !(i && i.pin); });

    var count = Math.min(opts.count || 1, live.length);
    var need = Math.max(0, count - pinned.length);
    if (!rest.length || !need) return pinned.slice(0, count);

    var order = shuffled(rest.length, seedFor(opts, now));
    var step = opts.visit === false ? 0 : visitTick(key, true);
    var at = (step * need) % rest.length;

    var out = pinned.slice(0, count), used = {};
    for (var k = 0; out.length < count && k < rest.length; k++) {
      var i = order[(at + k) % rest.length];
      if (used[i]) continue;
      used[i] = 1; out.push(rest[i]);
    }
    return out;
  }

  /* ---- declarative mode: mark up the HTML, RÓTA does the rest ----
     <div data-rota="facts" data-rota-count="6" data-rota-visit="true"></div>
     ROTA.pool('facts', [...]);  ROTA.paint();
  */
  var POOLS = {};
  function pool(name, items) { POOLS[name] = items; return ROTA; }

  function paint(scope) {
    var nodes = (scope || document).querySelectorAll('[data-rota]');
    [].forEach.call(nodes, function (el) {
      var name = el.getAttribute('data-rota');
      var items = POOLS[name];
      if (!items) return;
      var got = pick(items, {
        key: name + (el.id ? '#' + el.id : ''),
        count: parseInt(el.getAttribute('data-rota-count'), 10) || 1,
        day: el.getAttribute('data-rota-day') !== 'false',
        band: el.getAttribute('data-rota-band') !== 'false',
        visit: el.getAttribute('data-rota-visit') !== 'false'
      });
      var render = ROTA.renderers[el.getAttribute('data-rota-render') || 'default'];
      el.innerHTML = got.map(render).join('');
      el.setAttribute('data-rota-band-now', bandName());
    });
    return ROTA;
  }

  var ROTA = {
    version: '1.0',
    PHI: PHI,
    rng: rng, hash: hash,
    dayIndex: dayIndex, band: band, bandName: bandName,
    pick: pick, pool: pool, paint: paint,
    renderers: {
      default: function (item) {
        if (typeof item === 'string') return '<div class="rota-item">' + item + '</div>';
        return '<article class="rota-item">' +
          (item.title ? '<h3>' + item.title + '</h3>' : '') +
          (item.body ? '<p>' + item.body + '</p>' : '') + '</article>';
      }
    },
    /* swap what a visitor is looking at without a reload */
    turn: function (scope) { return paint(scope); }
  };

  return ROTA;
}));
